import styled from "@emotion/styled";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PageWithTitle } from "src/Common";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink } from "react-router-dom";
import {
  EXCHANGES_STATUS,
  CALCULATOR_STATS,
  CONNECTOR_HEALTH,
} from "src/Api/endpoints";
import { useQuery } from "react-query";
import {
  getExchangesStatus,
  getCalculatorStats,
  getConnectorHealth,
} from "src/Api";
import { ExchangesStatusTable } from "../components/ExchangesStatusTable";
import humanizeDuration from "humanize-duration";
import { useMemo, useState } from "react";

export const ExchangesStatus = () => {
  const [instancesExpanded, setInstancesExpanded] = useState(true);
  const [expandedExchanges, setExpandedExchanges] = useState<Record<string, boolean>>({});
  const { data, isLoading } = useQuery(EXCHANGES_STATUS, getExchangesStatus);
  const { data: calcStats, isLoading: calcLoading } = useQuery(
    CALCULATOR_STATS,
    getCalculatorStats,
    { refetchInterval: 30000 }
  );
  const { data: connectorHealth, isLoading: connectorLoading } = useQuery(
    CONNECTOR_HEALTH,
    getConnectorHealth,
    { refetchInterval: 30000 }
  );

  // Total stablecoin pairs across all exchanges (before instance partitioning)
  const totalAvailablePairs = connectorHealth?.summary?.totalAvailablePairs ?? 0;

  // Calculate data loss between stages
  const subscribedPairs = connectorHealth?.summary?.totalSubscribedPairs ?? 0;
  const initializedPairs = connectorHealth?.summary?.totalInitializedPairs ?? 0;
  const calculatorPairs = calcStats?.totalOrderbooks ?? 0;

  const subscribeLossPercent =
    totalAvailablePairs > 0
      ? (((totalAvailablePairs - subscribedPairs) / totalAvailablePairs) * 100).toFixed(1)
      : "0";
  const initLossPercent =
    subscribedPairs > 0
      ? (((subscribedPairs - initializedPairs) / subscribedPairs) * 100).toFixed(1)
      : "0";
  const calcLossPercent =
    initializedPairs > 0
      ? (((initializedPairs - calculatorPairs) / initializedPairs) * 100).toFixed(1)
      : "0";

  // Calculate per-exchange summary from connector health data
  const exchangeSummary = useMemo(() => {
    if (!connectorHealth?.connectors) return {};
    
    const summary: Record<string, {
      instanceCount: number;
      totalDisconnects: number;
      lastConnectedAt: string | null;
      instances: any[];
    }> = {};
    
    Object.values(connectorHealth.connectors).forEach((connector: any) => {
      const exchange = connector.exchange;
      if (!summary[exchange]) {
        summary[exchange] = {
          instanceCount: 0,
          totalDisconnects: 0,
          lastConnectedAt: null,
          instances: []
        };
      }
      summary[exchange].instanceCount++;
      summary[exchange].totalDisconnects += connector.connection?.disconnectCount ?? 0;
      summary[exchange].instances.push(connector);
      
      // Track most recent lastConnectedAt
      const connectorLastConnected = connector.connection?.lastConnectedAt;
      const currentLastConnected = summary[exchange].lastConnectedAt;
      if (connectorLastConnected) {
        if (!currentLastConnected || connectorLastConnected > currentLastConnected) {
          summary[exchange].lastConnectedAt = connectorLastConnected;
        }
      }
    });
    
    return summary;
  }, [connectorHealth]);

  const exchangeKeys = Object.keys(exchangeSummary);
  const allExchangesExpanded =
    exchangeKeys.length > 0 &&
    exchangeKeys.every((ex) => expandedExchanges[ex] !== false);

  const toggleAllExchanges = () => {
    const next = !allExchangesExpanded;
    const nextState: Record<string, boolean> = {};
    exchangeKeys.forEach((ex) => {
      nextState[ex] = next;
    });
    setExpandedExchanges(nextState);
  };

  const handleAccordionChange = (exchange: string) => (_: unknown, isExpanded: boolean) => {
    setExpandedExchanges((prev) => ({ ...prev, [exchange]: isExpanded }));
  };

  return (
    <PageWithTitle
      title={
        <Stack spacing={2}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component={RouterLink}
              underline="hover"
              key="1"
              color="inherit"
              to="/arbitrage"
            >
              Arbitrage
            </Link>
            <Typography key="2" color="text.primary">
              Exchanges status <Title>( {data?.length ?? 0} )</Title>
            </Typography>
          </Breadcrumbs>
        </Stack>
      }
      style={{ padding: "2rem 5%" }}
    >
      {/* Data Pipeline Flow */}
      <PipelineSection>
        <SectionTitle>Data Pipeline</SectionTitle>
        <PipelineContainer>
          {/* Stage 0: Available */}
          <PipelineStage>
            <StageValue color="secondary">
              {connectorLoading ? "-" : totalAvailablePairs}
            </StageValue>
            <StageLabel>Available</StageLabel>
            <StageSubLabel>stablecoin pairs</StageSubLabel>
          </PipelineStage>

          <PipelineArrow loss={Number(subscribeLossPercent) > 5}>
            <ArrowForwardIcon />
            {Number(subscribeLossPercent) > 0 && (
              <LossIndicator loss={Number(subscribeLossPercent) > 5}>
                -{subscribeLossPercent}%
              </LossIndicator>
            )}
          </PipelineArrow>

          {/* Stage 1: Subscribed */}
          <PipelineStage>
            <StageValue color="info">
              {connectorLoading ? "-" : subscribedPairs}
            </StageValue>
            <StageLabel>Subscribed</StageLabel>
            <StageSubLabel>from connectors</StageSubLabel>
          </PipelineStage>

          <PipelineArrow loss={Number(initLossPercent) > 5}>
            <ArrowForwardIcon />
            {Number(initLossPercent) > 0 && (
              <LossIndicator loss={Number(initLossPercent) > 5}>
                -{initLossPercent}%
              </LossIndicator>
            )}
          </PipelineArrow>

          {/* Stage 2: Initialized */}
          <PipelineStage>
            <StageValue color="warning">
              {connectorLoading ? "-" : initializedPairs}
            </StageValue>
            <StageLabel>Initialized</StageLabel>
            <StageSubLabel>with snapshots</StageSubLabel>
          </PipelineStage>

          <PipelineArrow loss={Number(calcLossPercent) > 5}>
            <ArrowForwardIcon />
            {Number(calcLossPercent) > 0 && (
              <LossIndicator loss={Number(calcLossPercent) > 5}>
                -{calcLossPercent}%
              </LossIndicator>
            )}
          </PipelineArrow>

          {/* Stage 3: Calculator */}
          <PipelineStage>
            <StageValue color="success">
              {calcLoading ? "-" : calculatorPairs}
            </StageValue>
            <StageLabel>Calculator</StageLabel>
            <StageSubLabel>processing</StageSubLabel>
          </PipelineStage>
        </PipelineContainer>
      </PipelineSection>

      <Divider sx={{ my: 3 }} />

      {/* Calculator Stats */}
      <SectionTitle>Calculator Stats</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent>
              <StatsValue>
                {calcLoading ? "-" : calcStats?.totalPairs ?? 0}
              </StatsValue>
              <StatsLabel>Unique Pairs</StatsLabel>
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent>
              <StatsValue>
                {calcLoading ? "-" : calcStats?.totalSymbols ?? 0}
              </StatsValue>
              <StatsLabel>Symbols</StatsLabel>
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent>
              <StatsValue>
                {calcLoading ? "-" : calcStats?.pairsWithMultipleExchanges ?? 0}
              </StatsValue>
              <StatsLabel>Multi-Exchange</StatsLabel>
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent>
              <StatsValue>
                {connectorLoading
                  ? "-"
                  : connectorHealth?.summary?.totalRedisErrors ?? 0}
              </StatsValue>
              <StatsLabel error>Redis Errors</StatsLabel>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Exchange Connection Summary */}
      {Object.keys(exchangeSummary).length > 0 && (
        <>
          <SectionTitle>Connection Summary by Exchange</SectionTitle>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(exchangeSummary).map(([exchange, summary]: [string, any]) => (
              <Grid item xs={6} sm={3} key={exchange}>
                <ExchangeSummaryCard hasDisconnects={summary.totalDisconnects > 0}>
                  <CardContent>
                    <ExchangeName>{exchange}</ExchangeName>
                    <SummaryRow>
                      <SummaryLabel>Instances</SummaryLabel>
                      <SummaryValue>{summary.instanceCount}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Total Disconnects</SummaryLabel>
                      <SummaryValue error={summary.totalDisconnects > 0}>
                        {summary.totalDisconnects}
                      </SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Last On</SummaryLabel>
                      <SummaryTime>
                        {summary.lastConnectedAt
                          ? new Date(summary.lastConnectedAt).toLocaleTimeString()
                          : '-'}
                      </SummaryTime>
                    </SummaryRow>
                  </CardContent>
                </ExchangeSummaryCard>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Connector Instances - Connection Metrics (grouped by exchange, collapsible, scrollable) */}
      {connectorHealth?.connectors && Object.keys(connectorHealth.connectors).length > 0 && (
        <>
          <InstancesSectionHeader>
            <SectionTitle sx={{ marginBottom: 0 }}>Connector Instances</SectionTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={toggleAllExchanges}
                disabled={exchangeKeys.length === 0}
              >
                {allExchangesExpanded ? "Colapsar todo" : "Expandir todo"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setInstancesExpanded((e) => !e)}
              >
                {instancesExpanded ? "Minimizar" : "Expandir"}
              </Button>
            </Stack>
          </InstancesSectionHeader>
          <InstancesSection expanded={instancesExpanded}>
            {Object.entries(exchangeSummary)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([exchange, summary]: [string, any]) => (
                <Accordion
                  key={exchange}
                  expanded={expandedExchanges[exchange] !== false}
                  onChange={handleAccordionChange(exchange)}
                  sx={{
                    "&:before": { display: "none" },
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                    "& + &": { mt: 0 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <ConnectorExchange>{exchange}</ConnectorExchange>
                    <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                      {summary.instanceCount} instancia{summary.instanceCount !== 1 ? "s" : ""}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <ConnectorGrid>
                      {summary.instances.map((connector: any) => (
                        <ConnectorCard key={connector.instanceId || connector.instanceName}>
                          <ConnectorHeader>
                            <ConnectorInstance>
                              {connector.instanceName || `Instance ${connector.instanceId}`}
                            </ConnectorInstance>
                            <StatusBadge status={connector.status}>
                              {connector.status}
                            </StatusBadge>
                          </ConnectorHeader>
                          <ConnectorMetrics>
                            <MetricRow>
                              <MetricLabel>Last Connected</MetricLabel>
                              <MetricText>
                                {connector.connection?.lastConnectedAt
                                  ? new Date(connector.connection.lastConnectedAt).toLocaleString()
                                  : "Never"}
                              </MetricText>
                            </MetricRow>
                            <MetricRow>
                              <MetricLabel>Connects / Disconnects</MetricLabel>
                              <MetricText>
                                <span style={{ color: "#4caf50" }}>
                                  {connector.connection?.connectCount ?? 0}
                                </span>
                                {" / "}
                                <span
                                  style={{
                                    color:
                                      connector.connection?.disconnectCount > 0
                                        ? "#f44336"
                                        : "inherit",
                                  }}
                                >
                                  {connector.connection?.disconnectCount ?? 0}
                                </span>
                              </MetricText>
                            </MetricRow>
                            {connector.connection?.lastDisconnectedAt && (
                              <MetricRow>
                                <MetricLabel>Last Disconnect</MetricLabel>
                                <MetricText error>
                                  {new Date(connector.connection.lastDisconnectedAt).toLocaleString()}
                                  {connector.connection.lastDisconnectReason && (
                                    <DisconnectReason>
                                      ({connector.connection.lastDisconnectReason})
                                    </DisconnectReason>
                                  )}
                                </MetricText>
                              </MetricRow>
                            )}
                            <MetricRow>
                              <MetricLabel>Session Duration</MetricLabel>
                              <MetricText>
                                {connector.connection?.currentSessionDurationMs
                                  ? humanizeDuration(
                                      connector.connection.currentSessionDurationMs,
                                      { maxDecimalPoints: 0, largest: 2 }
                                    )
                                  : "-"}
                              </MetricText>
                            </MetricRow>
                            <MetricRow>
                              <MetricLabel>Subscribed Pairs</MetricLabel>
                              <MetricText>{connector.subscribedPairs ?? "-"}</MetricText>
                            </MetricRow>
                          </ConnectorMetrics>
                        </ConnectorCard>
                      ))}
                    </ConnectorGrid>
                  </AccordionDetails>
                </Accordion>
              ))}
          </InstancesSection>
          <Divider sx={{ my: 3 }} />
        </>
      )}

      {/* Exchange Status Table */}
      <SectionTitle>Exchange Details</SectionTitle>
      <ExchangesStatusTable
        data={data ?? []}
        isLoading={isLoading}
        calculatorCoverage={calcStats?.exchangeCoverage}
      />
    </PageWithTitle>
  );
};

const Title = styled.span(({ theme }: any) => ({
  color: theme.palette.primary.main,
  marginLeft: "5px",
}));

const SectionTitle = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase" as const,
  letterSpacing: "1px",
  marginBottom: "1rem",
}));

const PipelineSection = styled(Box)({
  marginBottom: "1rem",
});

const PipelineContainer = styled(Box)(({ theme }: any) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  padding: "1.5rem",
  background: theme.palette.background.paper,
  borderRadius: "12px",
  flexWrap: "wrap" as const,
}));

const PipelineStage = styled(Box)({
  textAlign: "center",
  minWidth: "120px",
});

const StageValue = styled(Typography)<{ color?: string }>(
  ({ theme, color }: any) => ({
    fontSize: "2.5rem",
    fontWeight: 700,
    color:
      color === "success"
        ? theme.palette.success.main
        : color === "warning"
          ? theme.palette.warning.main
          : color === "error"
            ? theme.palette.error.main
            : color === "secondary"
              ? theme.palette.secondary.main
              : theme.palette.info.main,
  })
);

const StageLabel = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.85rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const StageSubLabel = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
}));

const PipelineArrow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "loss",
})<{ loss?: boolean }>(({ theme, loss }: any) => ({
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  color: loss ? theme.palette.error.main : theme.palette.text.secondary,
  "& svg": {
    fontSize: "2rem",
  },
}));

const LossIndicator = styled(Typography)<{ loss?: boolean }>(
  ({ theme, loss }: any) => ({
    fontSize: "0.7rem",
    fontWeight: 600,
    color: loss ? theme.palette.error.main : theme.palette.warning.main,
  })
);

const StatsCard = styled(Card)(({ theme }: any) => ({
  background: theme.palette.background.paper,
  borderRadius: "8px",
  textAlign: "center" as const,
}));

const StatsValue = styled(Typography)(({ theme }: any) => ({
  fontSize: "1.8rem",
  fontWeight: 700,
  color: theme.palette.primary.main,
}));

const StatsLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "error",
})<{ error?: boolean }>(({ theme, error }: any) => ({
  fontSize: "0.7rem",
  color: error ? theme.palette.error.main : theme.palette.text.secondary,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
}));

// Instances section - collapsible & scrollable
const InstancesSectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "1rem",
  marginBottom: "1rem",
});

const InstancesSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  maxHeight: expanded ? "60vh" : 0,
  overflow: expanded ? "auto" : "hidden",
  transition: "max-height 0.3s ease",
}));

// Connector Instance Styles
const ConnectorGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "1rem",
  marginBottom: "1rem",
});

const ConnectorCard = styled(Card)(({ theme }: any) => ({
  background: theme.palette.background.paper,
  borderRadius: "8px",
  padding: "1rem",
}));

const ConnectorHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginBottom: "0.75rem",
  flexWrap: "wrap" as const,
});

const ConnectorExchange = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.9rem",
  fontWeight: 700,
  color: theme.palette.primary.main,
  textTransform: "uppercase" as const,
}));

const ConnectorInstance = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
}));

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status?: string }>(({ theme, status }: any) => ({
  fontSize: "0.65rem",
  fontWeight: 600,
  padding: "2px 8px",
  borderRadius: "12px",
  marginLeft: "auto",
  backgroundColor:
    status === "connected"
      ? theme.palette.success.main + "20"
      : status === "connecting"
        ? theme.palette.warning.main + "20"
        : theme.palette.error.main + "20",
  color:
    status === "connected"
      ? theme.palette.success.main
      : status === "connecting"
        ? theme.palette.warning.main
        : theme.palette.error.main,
}));

const ConnectorMetrics = styled(Box)({
  display: "flex",
  flexDirection: "column" as const,
  gap: "0.35rem",
});

const MetricRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const MetricLabel = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
}));

const MetricText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "error",
})<{ error?: boolean }>(({ theme, error }: any) => ({
  fontSize: "0.75rem",
  fontWeight: 500,
  color: error ? theme.palette.error.main : theme.palette.text.primary,
}));

const DisconnectReason = styled("span")(({ theme }: any) => ({
  fontSize: "0.65rem",
  marginLeft: "0.25rem",
  color: theme.palette.text.secondary,
}));

// Exchange Summary Card Styles
const ExchangeSummaryCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "hasDisconnects",
})<{ hasDisconnects?: boolean }>(({ theme, hasDisconnects }: any) => ({
  background: theme.palette.background.paper,
  borderRadius: "8px",
  border: hasDisconnects 
    ? `1px solid ${theme.palette.warning.main}40` 
    : "1px solid transparent",
}));

const ExchangeName = styled(Typography)(({ theme }: any) => ({
  fontSize: "1rem",
  fontWeight: 700,
  color: theme.palette.primary.main,
  textTransform: "uppercase" as const,
  marginBottom: "0.5rem",
  textAlign: "center" as const,
}));

const SummaryRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "0.25rem",
});

const SummaryLabel = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
}));

const SummaryValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "error",
})<{ error?: boolean }>(({ theme, error }: any) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: error ? theme.palette.error.main : theme.palette.text.primary,
}));

const SummaryTime = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
}));
